import {createAction} from 'redux-actions';
import classnames from 'classnames';
import uuid from 'uuid';
import {
  getCurrentDesignId,
  getLastNamesIconId,
  getLastNumbersIconId,
  namesIconSelector,
  numbersIconSelector
} from '~/selectors/CanvasSelectors';
import {getNamesAndNumbers} from '~/selectors/NamesAndNumbersSelectors';
import {
  getDefaultIconColor,
  getViewsList,
  getViewsOptions
} from '~/selectors/ProductSelectors';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import NamesAndNumbersTypes from '~/constants/types/NamesAndNumbersTypes';
import {namesAndNumbersAlert} from '~/actions/AnalyticsActions';
import {
  NAMES_DEFAULT_Y_FACTOR,
  NAMES_FONT_HEIGHTS,
  NUMBERS_DEFAULT_Y_OFFSET,
  NUMBERS_FONT_HEIGHTS
} from '~/constants/NamesAndNumbersConstants';

export const remove = createAction(NamesAndNumbersTypes.DELETE);

export const removeAll = () => (dispatch, getState) => {
  const state = getState();
  const iconIds = [];
  const namesIcon = namesIconSelector(state);
  const numbersIcon = numbersIconSelector(state);
  if (namesIcon) {
    iconIds.push(namesIcon.get('id'));
  }
  if (numbersIcon) {
    iconIds.push(numbersIcon.get('id'));
  }
  dispatch({
    type: NamesAndNumbersTypes.CLEAR,
    payload: {
      designId: getCurrentDesignId(state),
      iconIds
    }
  });
};

export function addNames(viewId) {
  return (dispatch, getState) => {
    const iconId = uuid.v4();
    const state = getState();
    const designId = getCurrentDesignId(state);
    const backViewAvailable = getViewsOptions(state).find(
      view => view.value === VIEW_ID_MAP.back
    );
    if (!getLastNamesIconId(state) && backViewAvailable) {
      viewId = VIEW_ID_MAP.back;
    }
    const view = getViewsList(state).find(view => view.id === viewId);
    const color = getDefaultIconColor(state);
    const y = NAMES_DEFAULT_Y_FACTOR * view.constraint.height;
    const fontHeight = NAMES_FONT_HEIGHTS[NAMES_FONT_HEIGHTS.length - 1];
    return dispatch({
      type: NamesAndNumbersTypes.ADD_NAMES,
      payload: {designId, iconId, viewId, color, y, fontHeight}
    });
  };
}

export function addNumbers(viewId) {
  return (dispatch, getState) => {
    const iconId = uuid.v4();
    const state = getState();
    const designId = getCurrentDesignId(state);
    const backViewAvailable = getViewsOptions(state).find(
      view => view.value === VIEW_ID_MAP.back
    );
    if (!getLastNumbersIconId(state) && backViewAvailable) {
      viewId = VIEW_ID_MAP.back;
    }
    const view = getViewsList(state).find(view => view.id === viewId);
    const color = getDefaultIconColor(state);
    const y =
      NAMES_DEFAULT_Y_FACTOR * view.constraint.height +
      NUMBERS_DEFAULT_Y_OFFSET;
    const fontHeight = NUMBERS_FONT_HEIGHTS[NUMBERS_FONT_HEIGHTS.length - 1];
    return dispatch({
      type: NamesAndNumbersTypes.ADD_NUMBERS,
      payload: {designId, iconId, viewId, color, y, fontHeight}
    });
  };
}

export function addNamesAndNumbers() {
  return (dispatch, getState) => {
    const namesIconId = uuid.v4();
    const numbersIconId = uuid.v4();
    const state = getState();
    const designId = getCurrentDesignId(state);
    const backViewAvailable = getViewsOptions(state).find(
      view => view.value === VIEW_ID_MAP.back
    );
    const viewId = backViewAvailable ? VIEW_ID_MAP.back : VIEW_ID_MAP.front;
    const view = getViewsList(state).find(view => view.id === viewId);
    const color = getDefaultIconColor(state);
    const namesY = NAMES_DEFAULT_Y_FACTOR * view.constraint.height;
    const numbersY =
      NAMES_DEFAULT_Y_FACTOR * view.constraint.height +
      NUMBERS_DEFAULT_Y_OFFSET;
    const namesFontHeight = NAMES_FONT_HEIGHTS[NAMES_FONT_HEIGHTS.length - 1];
    const numbersFontHeight =
      NUMBERS_FONT_HEIGHTS[NUMBERS_FONT_HEIGHTS.length - 1];
    return dispatch({
      type: NamesAndNumbersTypes.ADD_NAMES_AND_NUMBERS,
      payload: {
        color,
        designId,
        namesFontHeight,
        namesIconId,
        namesY,
        numbersIconId,
        numbersFontHeight,
        numbersY,
        viewId
      }
    });
  };
}

export const update = createAction(NamesAndNumbersTypes.UPDATE);

export function create(payload) {
  return (dispatch, getState) => {
    const state = getState();
    const namesAndNumbers = getNamesAndNumbers(state);
    const isDuplicateName = namesAndNumbers.find(
      nameAndNumber => nameAndNumber.name === payload.name
    );
    const isDuplicateNumber = namesAndNumbers.find(
      nameAndNumber => nameAndNumber.number === payload.number
    );
    if (isDuplicateName || isDuplicateNumber) {
      const dupestring = classnames('dupe', {
        name: isDuplicateName,
        number: isDuplicateNumber
      }).replace(/ /g, '');
      dispatch(namesAndNumbersAlert(dupestring));
    }
    return dispatch({
      type: NamesAndNumbersTypes.CREATE,
      payload
    });
  };
}

export const batchCreate = createAction(NamesAndNumbersTypes.BATCH_CREATE);
export const removeEmpties = createAction(
  NamesAndNumbersTypes.REMOVE_EMPTY_NAMES_AND_NUMBERS
);
export const removeInvalids = createAction(
  NamesAndNumbersTypes.REMOVE_INVALID_NAMES_AND_NUMBERS
);
